<template>
  <section class="graphs-section">
    <div>
      <b-loading
        :is-full-page="false"
        :active.sync="jsonTelemetryData.state.isLoading"
        :can-cancel="false"
      />
      <div v-if="!dataLoaded && !jsonTelemetryData.state.isLoading">
        <form
          style="text-align: center; display: inline-block"
          action
          @submit.prevent
        >
          <b-field
            :message="
              isEmpty
                ? 'No data is available in the selected range, please select a different range.'
                : 'Select the keys you would like to show on a graph. Start typing to see the available keys.'
            "
          >
            <b-taginput
              v-model="selectedKeys"
              :data="filteredTags"
              autocomplete
              :allow-new="false"
              :open-on-focus="true"
              icon="label"
              placeholder="Add a key to axis..."
              :disabled="completed.x && completed.y || isEmpty"
              max-height="6rem"
              @typing="getFilteredTags"
            />
          </b-field>
          <b-field>
            <div class="field has-addons has-addons-centered">
              <p class="control">
                <button
                  :disabled="selectedKeys.length == 0"
                  class="button is-dark is-rounded"
                  @click="
                    prototypeKeys.y = [...prototypeKeys.y, ...selectedKeys];
                    selectedKeys = [];
                    completed.y = true;
                    applyGraph();
                  "
                >
                  <span>Proceed</span>
                  <b-icon
                    pack="fas"
                    icon="arrow-right"
                    size="is-small"
                  />
                </button>
              </p>
            </div>
          </b-field>
        </form>
      </div>
    </div>
    <div
      ref="plotlyGraph"
      class="plotly-graph"
      :style="{ width: isEmbed ? 'auto' : '54rem' }"
    />
  </section>
</template>

<script>
import Plotly from "plotly.js-dist";
import { mapActions, mapState, mapGetters } from "vuex";
import TelemetryApi from "@/services/Telemetry";
import Errors from "@/services/Error";

const lineBreakMinutes = 90;

export default {
  components: {},
  props: {
    startDate: Date,
    endDate: Date,
    deviceEui: String,
    passedDeviceName: String,
    extendedTelemetry: Boolean,
    apiKey: {
      type: String,
      default: null,
    },
    keys: {
      type: Array,
      default: null,
    },
    isEmbed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    let platform_id = process.env.VUE_APP_PLATFORM_ID;
    return {
      completed: {
        x: false,
        y: false,
      },
      selectedKeys: [],
      filteredTags: [],
      availableKeys: [],
      prototypeKeys: {
        x: [],
        y: [],
      },
      graphKeys: {
        x: [],
        y: [],
      },
      tempData: {},
      dataLoaded: false,
      data: [],
      layout: {
        title: "Unnamed device",
        showlegend: true,
        margin: { t: 40 },
      },
      options: {
        displaylogo: false,
        responsive: true,
        showSendToCloud: false,
        editable: !this.isEmbed,
        toImageButtonOptions: {
          format: "svg", // one of png, svg, jpeg, webp
          filename: platform_id + "-graph-" + this.passedDeviceName,
          height: 720,
          width: 1280,
          scale: 1, // Multiply title/legend/axis/canvas sizes by this factor
        },
        modeBarButtonsToRemove: [
          "pan2d",
          "select2d",
          "lasso2d",
          "resetScale2d",
          "zoomOut2d",
        ],
      },

      jsonResponse: null,
      requestStatus: null,
      requestError: null,
      isEmpty: false,
      dataSelector: {
        startDate: null,
        endDate: null,
        searchField: "",
        orderByDate: false,
      },
      jsonTelemetryData: {
        results: [],
        state: {
          isLoading: false,
          hasPrevResults: false,
        },
      },
    };
  },
  computed: {
    isLoginActive: {
      get() {
        return this.$store.state.loginActive;
      },
      set(value) {
        this.$store.commit("updateLoginActive", value);
      },
    },
    ...mapState({
      userState: "user",
    }),
    ...mapGetters(["UserLoggedIn"]),
  },
  methods: {
    ...mapActions({
      throwGlobalNotification: "throwGlobalNotification",
    }),
    applyGraph() {
      // setup X axis defaults
      this.prototypeKeys.x.push("Time axis");
      this.completed.x = true;

      // graph data setup
      for (let idx = 0; idx < this.prototypeKeys.y.length; idx++) {
        // reset keys
        this.graphKeys = { x: [], y: [] };

        this.tempData = {
          x: [],
          y: [],
          type: "scatter",
          mode: "lines+markers",
          connectgaps: false,
          name: "",
        };
        this.layout.title = this.passedDeviceName;

        // prepare the keys for one line
        this.graphKeys.y.push(this.prototypeKeys.y[idx]);
        this.graphKeys.x.push(this.prototypeKeys.x[0]);

        // get data for one line
        for (let i = 0; i < this.jsonTelemetryData.results.length; i++) {
          for (let y = 0; y < this.graphKeys.y.length; y++) {
            if (this.graphKeys.y[y] == "Time axis") {
              /* TODO: Deprecate Time axis on Y axis */
              this.tempData.y.push(
                this.jsonTelemetryData.results[i].date_created
              );
            } else if (
              this.graphKeys.y[y].includes("Metadata:") &&
              this.extendedTelemetry
            ) {
              let parameters = this.graphKeys.y[y].split(": ");
              let gateways = this.jsonTelemetryData.results[i]
                .payload_transmission.upstream_metadata.gateways;

              for (let idx = 0; idx < gateways.length; idx++) {
                if (parameters[1] == gateways[idx].gtw_id) {
                  switch (parameters[2]) {
                    case "RSSI":
                      this.tempData.y.push(gateways[idx].rssi);
                      this.tempData.name =
                        "RSSI (" + gateways[idx].gtw_id + ")";
                      break;
                    case "SNR":
                      this.tempData.y.push(gateways[idx].snr);
                      this.tempData.name = "SNR (" + gateways[idx].gtw_id + ")";
                      break;
                    case "RF Chain":
                      this.tempData.y.push(gateways[idx].rf_chain);
                      this.tempData.name =
                        "RF Chain (" + gateways[idx].gtw_id + ")";
                      break;
                    case "Channel":
                      this.tempData.y.push(gateways[idx].channel);
                      this.tempData.name =
                        "Channel (" + gateways[idx].gtw_id + ")";
                      break;
                  }
                  break;
                }
              }

              switch (parameters[1]) {
                case "Frequency":
                  this.tempData.y.push(
                    this.jsonTelemetryData.results[i].payload_transmission
                      .upstream_metadata.frequency
                  );
                  this.tempData.name = "Frequency";
                  break;
                case "Bit rate":
                  this.tempData.y.push(
                    this.jsonTelemetryData.results[i].payload_transmission
                      .upstream_metadata.bit_rate
                  );
                  this.tempData.name = "Bit rate";
                  break;
                case "Coding rate":
                  this.tempData.y.push(
                    this.jsonTelemetryData.results[i].payload_transmission
                      .upstream_metadata.coding_rate
                  );
                  this.tempData.name = "Coding rate";
                  break;
                case "Data rate":
                  this.tempData.y.push(
                    this.jsonTelemetryData.results[i].payload_transmission
                      .upstream_metadata.data_rate
                  );
                  this.tempData.name = "Data rate";
                  break;
                case "Modulation":
                  this.tempData.y.push(
                    this.jsonTelemetryData.results[i].payload_transmission
                      .upstream_metadata.modulation
                  );
                  this.tempData.name = "Modulation";
                  break;
              }
            } else {
              this.tempData.y.push(
                this.jsonTelemetryData.results[i].payload[this.graphKeys.y[y]]
              );
              this.tempData.name = this.graphKeys.y[y];
            }
          }
          for (let x = 0; x < this.graphKeys.x.length; x++) {
            if (this.graphKeys.x[x] == "Time axis") {
              this.tempData.x.push(
                this.jsonTelemetryData.results[i].date_created
              );

              /* Only implemented on the X axis */
              if (i != 0) {
                let curDate = new Date(
                  this.jsonTelemetryData.results[i].date_created
                );
                let prevDate = new Date(
                  this.jsonTelemetryData.results[i - 1].date_created
                );
                let diffMinutes = Math.ceil((curDate - prevDate) / (1000 * 60));

                // Change the number sign, in case it's negative (depends on data order)
                if (diffMinutes < 0) {
                  diffMinutes = diffMinutes * (-1)
                }

                if (diffMinutes >= lineBreakMinutes) {
                  let oldValueX = this.tempData.x.pop();
                  let oldValueY = this.tempData.y.pop();
                  // eslint-disable-next-line
                  console.log(
                    "Patching results, removed:",
                    oldValueX,
                    oldValueY
                  ); // remove last element
                  this.tempData.x.push(
                    this.jsonTelemetryData.results[i].date_created
                  );
                  this.tempData.y.push(null);
                  this.tempData.x.push(oldValueX);
                  this.tempData.y.push(oldValueY);
                }
              }
            } else {
              this.tempData.x.push(
                this.jsonTelemetryData.results[i].payload[this.graphKeys.x[x]]
              );
              this.tempData.name = this.graphKeys.x[x];
            }
          }
        }
        this.data.push(this.tempData);
      }

      this.completed = {
        x: false,
        y: false,
      };

      this.dataLoaded = true;

      Plotly.plot(this.$refs.plotlyGraph, this.data, this.layout, this.options);
    },
    extractKeys(keys, data) {
      for (var k in data) keys.includes(k) ? null : keys.push(k);
      return keys;
    },
    pushIfNotExists(keys, element) {
      keys.includes(element) ? null : keys.push(element);
      return keys;
    },
    async request() {
      this.jsonTelemetryData.state.isLoading = true;
      this.jsonTelemetryData.state.hasPrevResults = true;
      var startDate = new Date(this.startDate);
      var endDate = new Date(
        this.endDate.getFullYear(),
        this.endDate.getMonth(),
        this.endDate.getDate() + 1
      );

      try {
        var results = await TelemetryApi.telemetryRequest(
          this.deviceEui,
          this.extendedTelemetry,
          2000,
          0,
          startDate.toISOString(),
          endDate.toISOString(),
          this.dataSelector.orderByDate,
          this.apiKey
        );
      } catch (err) {
        this.jsonTelemetryData.state.isLoading = false;
        if (err.name == Errors.API.Unauthorized.name) {
          this.throwGlobalNotification({
            message: "You have to be logged in to access this data.",
            queue: false,
            isError: true,
          });
        } else if (err.name == Errors.API.UnexpectedErr.name) {
          this.throwGlobalNotification({
            message: "An unexpected error has occurred!",
            queue: false,
            isError: true,
          });
        } else if (err.name == Errors.API.ServerInternalErr.name) {
          this.throwGlobalNotification({
            message: "Internal server error has occurred!",
            queue: false,
            isError: true,
          });
        } else {
          this.throwGlobalNotification({
            message: "Couldn't connect to the API. Are you online?",
            queue: false,
            isError: true,
          });
        }
      }
      if (!results) {
        // do nothing if nothing was fetched from the server
        this.jsonTelemetryData.results = [];
        this.isEmpty = true;
        this.jsonTelemetryData.state.isLoading = false;
      } else {
        var keys = [];

        this.throwGlobalNotification({
          message: "Successfully fetched data points",
          queue: true,
          isError: false,
          debug: true,
        });

        for (let i = 0; i < results.length; i++) {
          results[i].payload = JSON.parse(results[i].payload);

          // Parse the date to show it in the local timezone
          results[i].date_created = new Date(results[i].date_created);

          keys = this.extractKeys(keys, results[i].payload);

          // check if the user is using extendedTelemetry
          if (this.extendedTelemetry) {
            if (i == results.length - 1) {
              // add common custom parameters
              keys.push("Metadata: Frequency");
              keys.push("Metadata: Bit rate");
              keys.push("Metadata: Coding rate");
              keys.push("Metadata: Data rate");
              keys.push("Metadata: Modulation");
            }

            let gateways =
              results[i].payload_transmission.upstream_metadata.gateways;
            for (let idx = 0; idx < gateways.length; idx++) {
              keys = this.pushIfNotExists(
                keys,
                "Metadata: " + gateways[idx].gtw_id + ": RSSI"
              );
              keys = this.pushIfNotExists(
                keys,
                "Metadata: " + gateways[idx].gtw_id + ": SNR"
              );
              keys = this.pushIfNotExists(
                keys,
                "Metadata: " + gateways[idx].gtw_id + ": RF Chain"
              );
              keys = this.pushIfNotExists(
                keys,
                "Metadata: " + gateways[idx].gtw_id + ": Channel"
              );
            }
          }
        }

        this.availableKeys = keys;
        this.filteredTags = keys;

        // add the timestamp to keys
        this.availableKeys.push("Time axis");

        this.jsonTelemetryData.results = results;
      }
      this.jsonTelemetryData.state.isLoading = false;
    },
    getFilteredTags(text) {
      this.filteredTags = this.availableKeys.filter((option) => {
        return option.toString().toLowerCase().includes(text.toLowerCase());
      });
    },
  },
  mounted: async function () {
    await this.request();
    if (this.keys != null) {
      this.prototypeKeys.y = [...this.prototypeKeys.y, ...this.keys];
      this.selectedKeys = [];
      this.completed.y = true;
      this.applyGraph();
    }
  },
};
</script>

<style>
.plotly-graph {
  margin-top: 0;
  margin-left: auto;
  margin-right: auto;
  width: auto;
}
.graphs-section {
  min-height: 9rem;
}
</style>



